import { NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CommonModule } from '@angular/common';

import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import {
    MsalGuard, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService,
    MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent
} from '@azure/msal-angular';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertModule } from 'ngx-bootstrap/alert';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { LaddaModule } from 'angular2-ladda';

import { HttpHelperDataService } from './services/http-helper-data.service';
import { CanActivateNavigation, SecurityService, RestInterceptor } from './services/security.service';
import { GlobalErrorHandlerService } from './services/global-error-handler.service';
import { WebApiDataService } from './services/webapi-data.service';
import { ProfileDataService } from './services/profile-data.service';
import { ValidatorService } from './services/validator.service';

import { BlockBaseComponent } from './components/block-base/block-base.component';
import { DataGridComponent } from './components/datagrid/datagrid.component';
import { BrowseComponent } from './components/browse/browse.component';
import { LoginComponent } from './components/login/login.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ChangePasswordComponent } from './components/profile/changepassword.component';
import { UnlockComponent } from './components/profile/unlock/unlock.component';
import { P500Component } from './components/pages/500.component';
import { P404Component } from './components/pages/404.component';

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    const config = window['appConfig'].Msal;
    return new PublicClientApplication({
        auth: {
            authority: config.Authority,
            clientId: config.ClienteId,
            redirectUri: config.RedirectUrl,

            // ------------------------------------------------
            // vGraf
            // ------------------------------------------------
            // clientId: 'fb33e854-2afc-4e52-9410-421818268003',
            // authority: 'https://login.microsoftonline.com/organizations',
            // redirectUri: 'http://localhost:4215/'

            // ------------------------------------------------
            // CTT - DEV
            // ------------------------------------------------
            // authority: 'https://login.microsoftonline.com/119ca644-d1ee-49a6-b0b4-43515deb6754',
            // clientId: '2937b38a-c751-46b9-a1d9-a3147c585573',
            // redirectUri: 'http://localhost:4215/'

            // ------------------------------------------------
            // CTT - QA
            // ------------------------------------------------
            // authority: 'https://login.microsoftonline.com/119ca644-d1ee-49a6-b0b4-43515deb6754',
            // clientId: '48b54332-5d4f-4d20-b2d5-4ca40bf56aff',
            // redirectUri: 'https://ctt-qa.boxer.pt/'

            // ------------------------------------------------
            // CTT - PRD
            // ------------------------------------------------
            // authority: 'https://login.microsoftonline.com/119ca644-d1ee-49a6-b0b4-43515deb6754',
            // clientId: 'dce0b307-6bb2-4461-befd-8733df6454c5',
            // redirectUri: 'https://ctt.boxer.pt/'
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false, // set to true for IE 11
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Info,
                piiLoggingEnabled: false
            }
        }
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['openid', 'user.read']);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: ['user.read']
        }
    };
}

@NgModule({
    declarations: [
        BrowseComponent,
        DataGridComponent,
        BlockBaseComponent,
        LoginComponent,
        ChangePasswordComponent,
        ProfileComponent,
        UnlockComponent,
        P404Component,
        P500Component
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MsalModule,
        BsDropdownModule.forRoot(),
        AlertModule.forRoot(),
        LaddaModule.forRoot({
            style: 'expand-right',
            spinnerSize: 23,
            spinnerColor: 'darkblue',
            spinnerLines: 12
        })
    ],
    exports: [
        BrowseComponent,
        DataGridComponent,
        BlockBaseComponent
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandlerService
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RestInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        NgbActiveModal,
        CanActivateNavigation,
        HttpHelperDataService,
        SecurityService,
        ValidatorService,
        WebApiDataService,
        ProfileDataService
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    bootstrap: [MsalRedirectComponent]
})

export class SharedModule { }
